import { ChangeDetectorRef, Directive, OnDestroy, OnInit } from '@angular/core';
import { IMetaCallResponse } from '@df/api/request/request.service';
import { HashStorageService } from '@df/browser/storage/hash-storage.service';
import { Review } from '@df/catalog/review/review';
import { ReviewResource } from '@df/catalog/review/review.resource';
import { App } from '@df/core/app';
import { Status } from '@df/core/status';
import { PagerComponentDef } from '@df/ui/display/pager.component.def';
import { Subscription } from 'rxjs';

@Directive()
export class AllReviewsComponentDef implements OnInit, OnDestroy {
    private subscription: Subscription = new Subscription();

    /**
     * Current page reviews
     */
    reviews?: Review[];

    /**
     * Load status
     */
    status = new Status();

    /**
     * Visible page
     */
    page = 0;

    /**
     * Number of pages on page
     */
    pagerSize = 20;

    /**
     * Information about review in db
     */
    meta?: IMetaCallResponse;

    /**
     * Page hash property
     */
    readonly pageHash = 'page';

    /**
     * Dependencies
     */
    constructor(
        private ref: ChangeDetectorRef,
        private app: App,
        private reviewResource: ReviewResource,
        private hashStorageService: HashStorageService
    ) {}

    /**
     * Reviews to show filter
     */
    protected getFilter() {
        return { verbosity: 3 };
    }

    /**
     * Initial listeners
     */
    ngOnInit(): void {
        // read initial page from hash
        this.page = this.hashStorageService.get(this.pageHash);

        // subscribe to status changes
        this.subscription.add(this.status.subscribe(() => this.detectChanges()));

        this.reviewResource.loadMeta({ filter: this.getFilter() }).then(meta => {
            this.meta = meta;
            this.loadPage();
            this.detectChanges();
        });
    }

    ngOnDestroy(): void {
        this.subscription.unsubscribe();
    }

    onPagerChange(pager: PagerComponentDef): void {
        this.setPage(pager.page);
    }

    private setPage(page: number): void {
        if (this.page !== page) {
            this.page = page;
            this.hashStorageService.set(this.pageHash, page);
            this.loadPage();
        }
    }

    /**
     * Translation of page size into offset
     */
    private get offset() {
        return this.pagerSize * (this.page - 1);
    }

    /**
     * Load current page reviews
     */
    private loadPage() {
        this.app.request.send(
            this.status,
            () => {
                return new Promise<void>(async (resolve) => {
                    try {
                        const reviews = await this.app.resources.review.loadMany({ verbosity: 3, limit: this.pagerSize, skip: this.offset });

                        this.setReviews(reviews);

                        resolve();
                    } catch (e) {
                        this.app.logger.error(e);
                        this.setReviews([]);

                        resolve();
                    }
                });
            },
            () => this.detectChanges(),
            () => this.detectChanges()
        );
    }

    /**
     * Current page reviews setter
     */
    private setReviews(reviews) {
        this.reviews = reviews;
    }

    /**
     * Tracking method for reviews
     */
    reviewTrackFn(index: number, review: Review): number | string {
        return review.id;
    }

    /**
     * Chagne detection
     */
    protected detectChanges(): void {
        this.ref.detectChanges();
    }
}
